import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import Pagination from "@/components/Pagination";
import { Plus, PencilSimple, Trash, Buildings, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

const emptyVendor = { name: "", contact_person: "", email: "", phone: "", address: "", gstin: "" };

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyVendor);
  const [saving, setSaving] = useState(false);

  const load = async (targetPage = page) => {
    setLoading(true);
    try {
      const params = { page: targetPage, page_size: pageSize };
      if (search) params.search = search;
      const { data } = await api.get("/vendors", { params });
      setVendors(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setPage(data.page || 1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1); }, []); // initial
  // Debounce search into the query
  useEffect(() => {
    const t = setTimeout(() => load(1), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyVendor);
    setOpen(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setForm({
      name: v.name || "",
      contact_person: v.contact_person || "",
      email: v.email || "",
      phone: v.phone || "",
      address: v.address || "",
      gstin: v.gstin || "",
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Vendor name required");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/vendors/${editing.id}`, form);
        toast.success("Vendor updated");
      } else {
        await api.post("/vendors", form);
        toast.success("Vendor added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}"?`)) return;
    try {
      await api.delete(`/vendors/${v.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="p-8 sm:p-10 max-w-[1400px] mx-auto" data-testid="vendors-page">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2">Directory</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Vendors</h1>
          <p className="text-sm text-neutral-500 mt-2">{total} supplier{total === 1 ? "" : "s"}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-sm h-11 px-5 bg-primary hover:bg-[#0039CC] gap-2" data-testid="add-vendor-btn">
              <Plus size={16} weight="bold" /> Add Vendor
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2">
                <Buildings size={20} /> {editing ? "Edit Vendor" : "Add Vendor"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Field label="Vendor Name *">
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-10" data-testid="vendor-dialog-name" />
              </Field>
              <Field label="Contact Person">
                <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="rounded-sm h-10" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm h-10" /></Field>
                <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm h-10" /></Field>
              </div>
              <Field label="GSTIN"><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="rounded-sm h-10" /></Field>
              <Field label="Address"><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm min-h-[80px]" /></Field>
              <Button className="w-full rounded-sm h-11 bg-primary hover:bg-[#0039CC]" onClick={handleSave} disabled={saving} data-testid="vendor-dialog-save">
                {saving ? "Saving…" : editing ? "Update Vendor" : "Save Vendor"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-neutral-200 rounded-sm p-4 mb-4">
        <div className="relative max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, contact, email, phone, GSTIN"
            className="rounded-sm pl-9 h-10"
            data-testid="vendors-search-input"
          />
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600">
              <tr>
                <th className="text-left px-6 py-3 font-medium text-xs uppercase tracking-wider">Vendor</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Contact</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Phone / Email</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">GSTIN</th>
                <th className="px-4 py-3 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-12 text-neutral-400">Loading…</td></tr>
              ) : vendors.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-16 text-neutral-400">No vendors yet. Click "Add Vendor" to get started.</td></tr>
              ) : (
                vendors.map((v, idx) => (
                  <tr key={v.id} className={`border-t border-neutral-100 hover:bg-neutral-50 ${idx % 2 ? "bg-neutral-50/40" : ""}`} data-testid={`vendor-row-${v.name}`}>
                    <td className="px-6 py-3 font-medium text-neutral-900">{v.name}</td>
                    <td className="px-4 py-3 text-neutral-700">{v.contact_person || "—"}</td>
                    <td className="px-4 py-3 text-neutral-600">
                      <div>{v.phone || "—"}</div>
                      <div className="text-xs text-neutral-500">{v.email || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-neutral-500 font-mono-num text-xs">{v.gstin || "—"}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button className="p-1.5 text-neutral-500 hover:text-primary" onClick={() => openEdit(v)} data-testid={`edit-vendor-${v.name}`}><PencilSimple size={16} /></button>
                      <button className="p-1.5 text-neutral-500 hover:text-destructive" onClick={() => handleDelete(v)} data-testid={`delete-vendor-${v.name}`}><Trash size={16} /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          onPageChange={(n) => load(n)}
          testIdPrefix="vendors"
        />
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wider text-neutral-600">{label}</Label>
      {children}
    </div>
  );
}
